import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

export function DutySummaryView(props) {
  var employeeId = props.employeeId;
  var [dutyData, setDutyData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  var [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(function() {
    setLoading(true);
    fetch(API_URL + '/api/employee/my-duty/' + employeeId + '?month=' + selectedMonth + '&year=' + selectedYear)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setDutyData(data);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }, [employeeId, selectedMonth, selectedYear]);

  var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function renderRecords() {
    if (!dutyData || !dutyData.records || dutyData.records.length === 0) {
      return <p className="text-gray-400 text-center py-4">No duty records for this month yet.</p>;
    }
    var items = [];
    for (var i = 0; i < dutyData.records.length; i++) {
      var r = dutyData.records[i];
      var statusClass = r.status === 'present' ? 'bg-green-500/20 text-green-400' :
                       r.status === 'absent' ? 'bg-red-500/20 text-red-400' :
                       r.status === 'half_day' ? 'bg-yellow-500/20 text-yellow-400' :
                       r.status === 'leave' ? 'bg-purple-500/20 text-purple-400' :
                       r.status === 'weekly_off' ? 'bg-blue-500/20 text-blue-400' :
                       'bg-pink-500/20 text-pink-400';
      items.push(
        <div key={r.date} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
          <span className="text-white">{new Date(r.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}</span>
          <Badge className={statusClass}>{r.status.replace(/_/g, ' ')}</Badge>
        </div>
      );
    }
    return <div className="space-y-2 max-h-64 overflow-y-auto">{items}</div>;
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-green-400" />
            My Duty Summary
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={selectedMonth.toString()} onValueChange={function(v) { setSelectedMonth(parseInt(v)); }}>
              <SelectTrigger className="bg-slate-700/50 border-white/10 text-white w-28"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/10">
                {months.map(function(m, i) { return <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={function(v) { setSelectedYear(parseInt(v)); }}>
              <SelectTrigger className="bg-slate-700/50 border-white/10 text-white w-24"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/10">
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin"></div>
          </div>
        ) : dutyData ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-gradient-to-br from-green-900/40 to-green-800/20 rounded-lg border border-green-500/30 text-center">
                <p className="text-3xl font-bold text-green-400">{dutyData.total_working_days}</p>
                <p className="text-sm text-gray-400">Working Days</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-red-900/40 to-red-800/20 rounded-lg border border-red-500/30 text-center">
                <p className="text-3xl font-bold text-red-400">{dutyData.total_off_days}</p>
                <p className="text-sm text-gray-400">Off Days</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-900/40 to-blue-800/20 rounded-lg border border-blue-500/30 text-center">
                <p className="text-3xl font-bold text-blue-400">{dutyData.summary ? dutyData.summary.weekly_off : 0}</p>
                <p className="text-sm text-gray-400">Weekly Offs</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-pink-900/40 to-pink-800/20 rounded-lg border border-pink-500/30 text-center">
                <p className="text-3xl font-bold text-pink-400">{dutyData.summary ? dutyData.summary.holiday : 0}</p>
                <p className="text-sm text-gray-400">Holidays</p>
              </div>
            </div>

            <div>
              <p className="text-white font-medium mb-3">Daily Records</p>
              {renderRecords()}
            </div>

            {dutyData.weekly_off_day && dutyData.weekly_off_day !== 'none' && (
              <div className="p-3 bg-blue-900/30 border border-blue-500/30 rounded-lg">
                <p className="text-blue-300 text-sm">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Your weekly off is on <strong>{dutyData.weekly_off_day.charAt(0).toUpperCase() + dutyData.weekly_off_day.slice(1)}</strong>
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">Unable to load duty data.</p>
        )}
      </CardContent>
    </Card>
  );
}
